import { useEffect } from "react";
import app from "nystem";

let pdfLib = false;
let jpeg2000Lib = false;
let jbig2Lib = false;

const add00 = (int) => (int < 10 ? `00${int}` : int < 100 ? `0${int}` : int);

const createProgressLogger = (label, total) => {
  let lastProgress = -1;

  return async (current) => {
    if (!total) return;

    const progress = Math.min(100, Math.round((100 * current) / total));
    if (
      progress === lastProgress ||
      (progress !== 0 && progress !== 100 && progress < lastProgress + 5)
    )
      return;

    lastProgress = progress;
    await app.event("fileUploadLog", {
      log: `${label}: ${current}/${total} (${progress} %)`,
    });
  };
};

const multiplyMatrices = ([a1, b1, c1, d1, e1, f1], [a2, b2, c2, d2, e2, f2]) => [
  a1 * a2 + c1 * b2,
  b1 * a2 + d1 * b2,
  a1 * c2 + c1 * d2,
  b1 * c2 + d1 * d2,
  a1 * e2 + c1 * f2 + e1,
  b1 * e2 + d1 * f2 + f1,
];

const rgbaFromTile = ({ items, width, height, componentsCount }) => {
  const pixels = width * height;
  const rgba = new Uint8ClampedArray(pixels * 4);

  for (let source = 0, target = 0; target < rgba.length; target += 4) {
    if (componentsCount === 1) {
      const value = items[source++];
      rgba[target] = value;
      rgba[target + 1] = value;
      rgba[target + 2] = value;
      rgba[target + 3] = 255;
      continue;
    }

    if (componentsCount === 2) {
      const value = items[source++];
      rgba[target] = value;
      rgba[target + 1] = value;
      rgba[target + 2] = value;
      rgba[target + 3] = items[source++];
      continue;
    }

    rgba[target] = items[source++];
    rgba[target + 1] = items[source++];
    rgba[target + 2] = items[source++];
    rgba[target + 3] = componentsCount > 3 ? items[source++] : 255;
  }

  return new ImageData(rgba, width, height);
};

const maskToImageData = ({ data, width, height }) => {
  const rowSize = (width + 7) >> 3;
  const rgba = new Uint8ClampedArray(width * height * 4);

  let source = 0;
  let target = 0;
  for (let y = 0; y < height; y++) {
    let mask = 0;
    let value = 0;
    for (let x = 0; x < width; x++) {
      if (!mask) {
        mask = 0x80;
        value = data[source++];
      }

      rgba[target + 3] = value & mask ? 255 : 0;
      target += 4;
      mask >>= 1;
    }

    source = (y + 1) * rowSize;
  }

  return new ImageData(rgba, width, height);
};

const decodeJpx = async (data) => {
  if (!jpeg2000Lib)
    jpeg2000Lib = await import("../../client/jpeg2000/jpx.js");

  const { JpxImage } = jpeg2000Lib;
  const jpx = new JpxImage();
  jpx.parse(new Uint8Array(data));

  const canvas = document.createElement("canvas");
  canvas.width = jpx.width;
  canvas.height = jpx.height;
  const context = canvas.getContext("2d");

  jpx.tiles.forEach((tile) => {
    context.putImageData(
      rgbaFromTile({
        ...tile,
        componentsCount: jpx.componentsCount,
      }),
      tile.left,
      tile.top,
    );
  });

  const blob = await new Promise((resolve, reject) =>
    canvas.toBlob(
      (result) => {
        if (!result) reject(new Error("Could not encode JPEG2000 image"));
        else resolve(result);
      },
      "image/jpeg",
      0.95,
    ),
  );

  return {
    data: new Uint8Array(await blob.arrayBuffer()),
    ext: "jpeg",
    width: canvas.width,
    height: canvas.height,
  };
};

const decodeJbig2Mask = async ({ data, width, height }) => {
  if (!jbig2Lib) jbig2Lib = await import("../../client/jbig2/jbig2.js");

  const { parseJbig2Chunks } = jbig2Lib;
  const bitPacked = parseJbig2Chunks([{ data: new Uint8Array(data), start: 0, end: data.length }]);

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  canvas.getContext("2d").putImageData(
    maskToImageData({ data: bitPacked, width, height }),
    0,
    0,
  );
  return canvas;
};

const getFilters = (pdfObject, PDFName) => {
  let filter = pdfObject.dict.get(PDFName.of("Filter"));
  if (!filter) return [];
  if (filter.array) return filter.array.map((item) => item.encodedName);
  return [filter.encodedName];
};

const getRefKey = (value) => {
  if (!value) return false;
  if (typeof value.toString === "function") return value.toString();
  return false;
};

const loadRasterImage = (data, type = "image/jpeg") =>
  new Promise((resolve, reject) => {
    const image = new Image();
    const url = URL.createObjectURL(new Blob([data], { type }));

    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error(`Could not decode ${type}`));
    };
    image.src = url;
  });

const inflate = async (data) => {
  const stream = new Blob([data]).stream().pipeThrough(new DecompressionStream("deflate"));
  return new Uint8Array(await new Response(stream).arrayBuffer());
};

const parseDrawOps = (content) => {
  const tokens =
    content.match(/\/[A-Za-z0-9_]+|[-+]?(?:\d*\.\d+|\d+)|[A-Za-z]{1,3}/g) || [];

  const stack = [];
  const ops = [];
  const numbers = [];
  let matrix = [1, 0, 0, 1, 0, 0];
  let lastName = false;

  tokens.forEach((token) => {
    if (/^[-+]?(?:\d*\.\d+|\d+)$/.test(token)) {
      numbers.push(parseFloat(token));
      return;
    }

    if (token[0] === "/") {
      lastName = token.substring(1);
      return;
    }

    if (token === "q") {
      stack.push([...matrix]);
      return;
    }

    if (token === "Q") {
      matrix = stack.pop() || [1, 0, 0, 1, 0, 0];
      return;
    }

    if (token === "cm" && numbers.length >= 6) {
      matrix = multiplyMatrices(matrix, numbers.splice(-6));
      return;
    }

    if (token === "Do" && lastName) {
      ops.push({ name: lastName, matrix: [...matrix] });
      return;
    }
  });

  return ops;
};

const getNumber = (pdfObject, PDFName, key) =>
  pdfObject.dict.get(PDFName.of(key))?.numberValue || 0;

const isPageSized = (image, largestArea) => {
  const { width, height } = image;
  const area = width * height;
  const shortest = Math.min(width, height);
  const longest = Math.max(width, height);

  if (!area || !shortest || !longest) return false;

  return area >= largestArea * 0.25 && longest / shortest <= 3;
};

const getImageUpload = async (image) => {
  if (image.filters.includes("/DCTDecode"))
    return {
      data: image.pdfObject.contents,
      ext: "jpeg",
      width: image.width,
      height: image.height,
    };
  if (image.filters.includes("/JPXDecode")) return decodeJpx(image.pdfObject.contents);
  return false;
};

const applyMask = async (source, maskImage) => {
  const base = await loadRasterImage(source.data, `image/${source.ext}`);
  const mask = await decodeJbig2Mask({
    data: maskImage.pdfObject.contents,
    width: maskImage.width,
    height: maskImage.height,
  });

  const canvas = document.createElement("canvas");
  canvas.width = source.width;
  canvas.height = source.height;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(base, 0, 0, canvas.width, canvas.height);
  ctx.globalCompositeOperation = "destination-in";
  ctx.drawImage(mask, 0, 0, canvas.width, canvas.height);

  const blob = await new Promise((resolve, reject) =>
    canvas.toBlob(
      (result) => {
        if (!result) reject(new Error("Could not encode masked image"));
        else resolve(result);
      },
      "image/png",
    ),
  );

  return {
    data: new Uint8Array(await blob.arrayBuffer()),
    ext: "png",
    width: canvas.width,
    height: canvas.height,
  };
};

const renderPage = async ({ content, images, mediaBox, allImages }) => {
  const ops = parseDrawOps(content);
  if (!ops.length) return false;

  const pageWidth = mediaBox[2] - mediaBox[0];
  const pageHeight = mediaBox[3] - mediaBox[1];
  if (!pageWidth || !pageHeight) return false;

  const canvas = document.createElement("canvas");
  canvas.width = Math.round(pageWidth);
  canvas.height = Math.round(pageHeight);
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  for (const op of ops) {
    const image = images[op.name];
    if (!image) continue;

    let source = await getImageUpload(image);
    if (source && image.maskRef) {
      const maskImage = allImages.get(image.maskRef);
      if (maskImage?.filters.includes("/JBIG2Decode"))
        source = await applyMask(source, maskImage);
    }
    if (!source) continue;

    const drawable =
      source.ext === "jpeg"
        ? await loadRasterImage(source.data, "image/jpeg")
        : await loadRasterImage(source.data, `image/${source.ext}`);

    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.translate(0, canvas.height);
    ctx.scale(1, -1);
    ctx.transform(...op.matrix);
    ctx.drawImage(drawable, 0, 0, 1, 1);
    ctx.restore();
  }

  const fixedCanvas = document.createElement("canvas");
  fixedCanvas.width = canvas.width;
  fixedCanvas.height = canvas.height;
  const fixedCtx = fixedCanvas.getContext("2d");
  fixedCtx.translate(0, fixedCanvas.height);
  fixedCtx.scale(1, -1);
  fixedCtx.drawImage(canvas, 0, 0);

  const blob = await new Promise((resolve, reject) =>
    fixedCanvas.toBlob(
      (result) => {
        if (!result) reject(new Error("Could not encode rendered page"));
        else resolve(result);
      },
      "image/jpeg",
      0.95,
    ),
  );

  return {
    data: new Uint8Array(await blob.arrayBuffer()),
    ext: "jpeg",
    width: fixedCanvas.width,
    height: fixedCanvas.height,
  };
};

const MediaPdfExtractor = ({ model, view }) => {
  useEffect(() => {
    const parsePdf = async (query) => {
      if (
        query.contentType !== view.contentType ||
        query.modelId !== model.id ||
        !query.data ||
        query.ext !== "pdf"
      )
        return;

      await app.event("fileUploadLog", { log: "PDF split started" });
      if (!pdfLib) pdfLib = (await import("../../client/pdfLib")).default;

      const { PDFArray, PDFDict, PDFDocument, PDFName, PDFRawStream } = pdfLib;

      const pdfDoc = await PDFDocument.load(query.data);

      const imageRefs = new Map();
      const pageObjects = [];
      pdfDoc.context
        .enumerateIndirectObjects()
        .map(([ref, pdfObject]) => {
          if (pdfObject instanceof PDFDict) {
            const type = pdfObject.get(PDFName.of("Type"));
            if (type?.encodedName === "/Page")
              pageObjects.push({ ref, page: pdfObject });
            return false;
          }
          if (!(pdfObject instanceof PDFRawStream)) return false;

          const subtype = pdfObject.dict.get(PDFName.of("Subtype"));
          if (!subtype || subtype.encodedName !== "/Image") return false;

          const filters = getFilters(pdfObject, PDFName);
          if (
            !filters.find((filter) =>
              ["/DCTDecode", "/JPXDecode", "/JBIG2Decode"].includes(filter),
            )
          )
            return false;

          const image = {
            ref: getRefKey(ref),
            pdfObject,
            filters,
            width: getNumber(pdfObject, PDFName, "Width"),
            height: getNumber(pdfObject, PDFName, "Height"),
            maskRef: getRefKey(pdfObject.dict.get(PDFName.of("Mask"))),
          };
          imageRefs.set(image.ref, image);
          if (pdfObject.dict.get(PDFName.of("ImageMask"))?.value) return false;
          return image;
        })
        .filter(Boolean);

      const renderedPages = [];
      const renderProgress = createProgressLogger("PDF split rendering pages", pageObjects.length);
      await renderProgress(0);

      for (let pageIndex = 0; pageIndex < pageObjects.length; pageIndex++) {
        try {
          const { page } = pageObjects[pageIndex];
          const resources = pdfDoc.context.lookupMaybe(
            page.get(PDFName.of("Resources")),
            PDFDict,
          );
          const xObjects = resources
            ? pdfDoc.context.lookupMaybe(resources.get(PDFName.of("XObject")), PDFDict)
            : false;
          const contents = page.get(PDFName.of("Contents"));
          const contentRefs = [];

          if (contents instanceof PDFArray)
            for (let index = 0; index < contents.size(); index++)
              contentRefs.push(contents.get(index));
          else if (contents) contentRefs.push(contents);

          const streams = [];
          for (const contentRef of contentRefs) {
            const stream = pdfDoc.context.lookupMaybe(contentRef, PDFRawStream);
            if (!stream) continue;

            const filters = getFilters(stream, PDFName);
            let bytes = stream.contents;
            if (filters.includes("/FlateDecode")) bytes = await inflate(bytes);
            streams.push(new TextDecoder().decode(bytes));
          }

          if (!streams.length || !xObjects) continue;

          const images = {};
          for (const [name, ref] of xObjects.entries()) {
            const image = imageRefs.get(getRefKey(ref));
            if (image) images[name.decodeText ? name.decodeText() : name.encodedName.substring(1)] = image;
          }

          const mediaBox = page.lookup(PDFName.of("MediaBox"), PDFArray)?.asArray?.();
          const box =
            mediaBox?.map((value) => value.numberValue) ||
            page.get(PDFName.of("MediaBox"))?.asArray?.().map((value) => value.numberValue);
          const rendered = await renderPage({
            content: streams.join("\n"),
            images,
            mediaBox: box,
            allImages: imageRefs,
          });
          if (rendered) renderedPages.push(rendered);
        } finally {
          await renderProgress(pageIndex + 1);
        }
      }

      if (renderedPages.length) {
        if (renderedPages.length > 1) {
          const mediaId = app.uuid();
          for (let index = 0; index < renderedPages.length; index++) {
            const upload = renderedPages[index];
            app.event("fileUpload", {
              ...query,
              id: app.uuid(),
              type: "image",
              ext: "jpeg",
              name: `${query.name}.${add00(index)}.jpeg`,
              data: upload.data,
              width: upload.width,
              height: upload.height,
              pos: index,
              mediaId,
            });
          }
          await app.event("fileUploadLog", { log: "PDF split done" });
          return { mediaId: query.mediaId };
        }

        const upload = renderedPages[0];
        return {
          ...query,
          data: upload.data,
          type: "image",
          ext: "jpeg",
          width: upload.width,
          height: upload.height,
          name: `${query.name}.jpeg`,
        };
      }

      const supportedImages = [...imageRefs.values()];

      const largestArea = supportedImages.reduce(
        (largest, image) => Math.max(largest, image.width * image.height),
        0,
      );
      const pageImages = supportedImages.filter((image) =>
        isPageSized(image, largestArea),
      );
      const images = pageImages.length ? pageImages : supportedImages;

      if (!images.length) return;

      if (images.length > 1) {
        console.log("Pdf image count", images.length);

        const mediaId = app.uuid();
        const extractProgress = createProgressLogger("PDF split extracting images", images.length);
        await extractProgress(0);

        for (let index = 0; index < images.length; index++) {
          const image = images[index];
          const upload = await getImageUpload(image);
          await extractProgress(index + 1);
          if (!upload) continue;

          app.event("fileUpload", {
            ...query,
            id: app.uuid(),
            type: "image",
            ext: upload.ext,
            name: `${query.name}.${add00(index)}.${upload.ext}`,
            data: upload.data,
            width: upload.width,
            height: upload.height,
            pos: index,
            mediaId,
          });
        }
        await app.event("fileUploadLog", { log: "PDF split done" });

        return { mediaId: query.mediaId };
      }

      const image = images[0];
      const upload = await getImageUpload(image);
      if (!upload) return;

      return {
        ...query,
        data: upload.data,
        type: "image",
        ext: upload.ext,
        width: upload.width,
        height: upload.height,
        name: `${query.name}.${upload.ext}`,
      };
    };

    app.on("fileUpload", 990, parsePdf);
    return () => {
      app.off("fileUpload", parsePdf);
    };
  });

  return null;
};
export default MediaPdfExtractor;
